import React, { useState, useEffect } from 'react';
import { AlertTriangle, Server, WifiOff, Cloud, Shield, XCircle, RefreshCw, CheckCircle } from 'lucide-react';
import { useServerHealth } from '../hooks/useServerHealth';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { enhancedSyncService, SyncStatusType, SyncProgress } from '../services/enhancedSyncService';
import { enhancedIndexedDBService } from '../services/enhancedIndexedDBService';

interface NetworkError {
  type: 'dns' | 'ssl' | 'server-down' | 'network-degraded';
  message: string;
  details?: any;
}

/**
 * Global banner that appears at top of screen when server is offline
 * Provides clear warning and guidance to users
 * Shows sync status with real-time progress
 */
const OfflineBanner: React.FC = () => {
  const { isOnline: networkOnline } = useOnlineStatus();
  const { isServerOnline } = useServerHealth(30000);
  const [networkError, setNetworkError] = useState<NetworkError | null>(null);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const [syncStatus, setSyncStatus] = useState<SyncStatusType>('idle');
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [isDbInitialized, setIsDbInitialized] = useState(false);

  // Wait for database initialization before loading stats
  useEffect(() => {
    const waitForDb = async () => {
      try {
        await enhancedIndexedDBService.waitForInit();
        setIsDbInitialized(true);
      } catch (error) {
        console.error('Failed to wait for DB initialization:', error);
      }
    };
    waitForDb();
  }, []);

  // Listen to sync status updates
  useEffect(() => {
    const handleSyncStatus = (event: CustomEvent<SyncStatusType>) => {
      setSyncStatus(event.detail);
    };

    const handleSyncProgress = (event: CustomEvent<SyncProgress>) => {
      setSyncProgress(event.detail);
    };

    window.addEventListener('sync-status-changed' as any, handleSyncStatus);
    window.addEventListener('sync-progress' as any, handleSyncProgress);

    // Initial load - get pending count (only after DB is initialized)
    const loadPendingCount = async () => {
      if (!isDbInitialized) return;
      
      try {
        const stats = await enhancedSyncService.getPendingStats();
        setPendingCount(stats.total);
      } catch (error) {
        // Silently fail on initial load - DB might not be ready yet
        console.debug('Pending count not available yet:', error);
      }
    };
    
    if (isDbInitialized) {
      loadPendingCount();
    }

    return () => {
      window.removeEventListener('sync-status-changed' as any, handleSyncStatus);
      window.removeEventListener('sync-progress' as any, handleSyncProgress);
    };
  }, [isDbInitialized]);

  // Update pending count when sync completes
  useEffect(() => {
    if (!isDbInitialized) return;
    
    if (syncStatus === 'completed' || syncStatus === 'failed') {
      const updateCount = async () => {
        try {
          const stats = await enhancedSyncService.getPendingStats();
          setPendingCount(stats.total);
        } catch (error) {
          console.error('Failed to update pending count:', error);
        }
      };
      updateCount();
    }
  }, [syncStatus, isDbInitialized]);

  // Network error listeners
  useEffect(() => {
    const handleDNSError = (event: any) => {
      setNetworkError({
        type: 'dns',
        message: event.detail?.message || 'DNS Resolution Error',
        details: event.detail
      });
    };

    const handleSSLError = (event: any) => {
      setNetworkError({
        type: 'ssl',
        message: event.detail?.message || 'SSL Certificate Error',
        details: event.detail
      });
    };

    const handleServerDown = (event: any) => {
      setNetworkError({
        type: 'server-down',
        message: event.detail?.message || 'Server Not Responding',
        details: event.detail
      });
    };

    const handleNetworkDegraded = (event: any) => {
      setConsecutiveFailures(event.detail?.consecutiveFailures || 0);
      setNetworkError({
        type: 'network-degraded',
        message: 'Koneksi tidak stabil. Beberapa request gagal berturut-turut.',
        details: event.detail
      });
    };

    window.addEventListener('dns-error', handleDNSError);
    window.addEventListener('ssl-error', handleSSLError);
    window.addEventListener('server-down', handleServerDown);
    window.addEventListener('network-degraded', handleNetworkDegraded);

    return () => {
      window.removeEventListener('dns-error', handleDNSError);
      window.removeEventListener('ssl-error', handleSSLError);
      window.removeEventListener('server-down', handleServerDown);
      window.removeEventListener('network-degraded', handleNetworkDegraded);
    };
  }, []);

  // Auto-clear network error after 10 seconds if connection is restored
  useEffect(() => {
    if (networkOnline && isServerOnline && networkError) {
      const timer = setTimeout(() => {
        setNetworkError(null);
        setConsecutiveFailures(0);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [networkOnline, isServerOnline, networkError]);

  // Don't show banner if everything is online and no errors and no pending sync
  if (networkOnline && isServerOnline && !networkError && syncStatus === 'idle' && pendingCount === 0) {
    return null;
  }

  // Manual sync trigger
  const handleManualSync = async () => {
    try {
      await enhancedSyncService.syncNow();
    } catch (error) {
      console.error('Manual sync failed:', error);
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 animate-slideDown">
      {/* Sync Progress Banner - Show when syncing */}
      {syncStatus === 'syncing' && syncProgress && (
        <div className="bg-blue-600 text-white py-2 px-4 shadow-lg">
          <div className="container mx-auto flex items-center gap-3">
            <RefreshCw size={20} className="flex-shrink-0 animate-spin" />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <p className="font-bold text-xs">🔄 SINKRONISASI DATA...</p>
                <span className="text-xs">
                  {syncProgress.current} / {syncProgress.total} ({Math.round((syncProgress.current / syncProgress.total) * 100)}%)
                </span>
              </div>
              <div className="w-full bg-blue-800 rounded-full h-1.5">
                <div
                  className="bg-white h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${(syncProgress.current / syncProgress.total) * 100}%` }}
                />
              </div>
              {syncProgress.eta && (
                <p className="text-xs mt-1 opacity-90">
                  Estimasi: {Math.ceil(syncProgress.eta / 1000)}s lagi
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pending Data Banner - Show when there's pending data to sync */}
      {pendingCount > 0 && syncStatus === 'idle' && networkOnline && isServerOnline && !networkError && (
        <div className="bg-green-600 text-white py-2 px-4 shadow-lg">
          <div className="container mx-auto flex items-center gap-3">
            <AlertTriangle size={20} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-xs">📦 {pendingCount} DATA BELUM TERSINKRON</p>
              <p className="text-xs opacity-90 mt-0.5">
                Auto-sync aktif. Klik tombol di samping untuk sync manual.
              </p>
            </div>
            <button
              onClick={handleManualSync}
              className="flex-shrink-0 bg-green-700 hover:bg-green-800 px-3 py-1 rounded text-xs font-semibold transition flex items-center gap-1"
            >
              <RefreshCw size={14} />
              Sync Sekarang
            </button>
          </div>
        </div>
      )}

      {/* Sync Complete Banner - Show briefly after successful sync */}
      {syncStatus === 'completed' && syncProgress && syncProgress.current === syncProgress.total && (
        <div className="bg-green-500 text-white py-2 px-4 shadow-lg">
          <div className="container mx-auto flex items-center gap-3">
            <CheckCircle size={20} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-xs">✅ SINKRONISASI BERHASIL</p>
              <p className="text-xs opacity-90 mt-0.5">
                {syncProgress.total} data berhasil tersinkron ke server.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* DNS Error Banner */}
      {networkError?.type === 'dns' && (
        <div className="bg-purple-600 text-white py-3 px-4 shadow-lg">
          <div className="container mx-auto flex items-center gap-3">
            <Cloud size={24} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-sm">⚠️ MASALAH DNS / CLOUDFLARE</p>
              <p className="text-xs mt-0.5">
                {networkError.message}
                <br />
                Coba: 1) Refresh halaman, 2) Periksa DNS (gunakan 8.8.8.8 atau 1.1.1.1), 3) Cek status Cloudflare.
              </p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="flex-shrink-0 bg-purple-700 hover:bg-purple-800 px-3 py-1 rounded text-xs font-semibold transition"
            >
              Refresh
            </button>
          </div>
        </div>
      )}

      {/* SSL Error Banner */}
      {networkError?.type === 'ssl' && (
        <div className="bg-red-700 text-white py-3 px-4 shadow-lg">
          <div className="container mx-auto flex items-center gap-3">
            <Shield size={24} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-sm">⚠️ KESALAHAN SERTIFIKAT SSL</p>
              <p className="text-xs mt-0.5">
                {networkError.message}
                <br />
                Sertifikat keamanan tidak valid. Periksa pengaturan SSL/TLS atau hubungi administrator.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Server Down Banner */}
      {networkError?.type === 'server-down' && (
        <div className="bg-red-600 text-white py-3 px-4 shadow-lg">
          <div className="container mx-auto flex items-center gap-3">
            <XCircle size={24} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-sm">⚠️ SERVER TIDAK MERESPONS</p>
              <p className="text-xs mt-0.5">
                {networkError.message}
                <br />
                Server mungkin sedang maintenance atau mengalami masalah. Transaksi akan disimpan otomatis.
              </p>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-block bg-red-800 px-3 py-1 rounded-full text-xs font-semibold">
                SERVER DOWN
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Network Degraded Banner */}
      {networkError?.type === 'network-degraded' && consecutiveFailures >= 3 && (
        <div className="bg-yellow-500 text-black py-3 px-4 shadow-lg">
          <div className="container mx-auto flex items-center gap-3">
            <AlertTriangle size={24} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-sm">⚠️ KONEKSI TIDAK STABIL</p>
              <p className="text-xs mt-0.5">
                {consecutiveFailures} request gagal berturut-turut. Koneksi internet atau server mungkin bermasalah.
                {networkError.details?.isCloudflareError && ' (Cloudflare Error)'}
                {networkError.details?.isDNSError && ' (DNS Error)'}
              </p>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-block bg-yellow-700 text-white px-3 py-1 rounded-full text-xs font-semibold">
                {consecutiveFailures} GAGAL
              </span>
            </div>
          </div>
        </div>
      )}

      {/* No Internet Connection Banner */}
      {!networkOnline && !networkError && (
        <div className="bg-red-600 text-white py-3 px-4 shadow-lg">
          <div className="container mx-auto flex items-center gap-3">
            <WifiOff size={24} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-sm">⚠️ TIDAK ADA KONEKSI INTERNET</p>
              <p className="text-xs mt-0.5">
                Periksa koneksi WiFi atau data seluler Anda. Transaksi akan disimpan dan di-sync otomatis saat online.
              </p>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-block bg-red-800 px-3 py-1 rounded-full text-xs font-semibold">
                MODE OFFLINE
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Server Offline Banner (when network is online but server unreachable) */}
      {networkOnline && !isServerOnline && !networkError && (
        <div className="bg-orange-500 text-white py-3 px-4 shadow-lg">
          <div className="container mx-auto flex items-center gap-3">
            <Server size={24} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-sm">⚠️ SERVER TIDAK DAPAT DIAKSES</p>
              <p className="text-xs mt-0.5">
                Koneksi internet ada, tetapi server POS tidak merespons. Transaksi akan disimpan dan di-sync otomatis saat server kembali.
              </p>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-block bg-orange-700 px-3 py-1 rounded-full text-xs font-semibold">
                SERVER OFFLINE
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfflineBanner;
